'use client';

import type { ComponentPropsWithoutRef, MouseEvent } from 'react';

type InPageLinkProps = Omit<ComponentPropsWithoutRef<'a'>, 'href'> & {
  targetId: string;
};

/**
 * Same-page anchor for hero buttons that point at a section further down.
 *
 * The root element has scroll-behavior: smooth, so scrollIntoView animates.
 * next/link would also animate, but only when the hash actually changes: a
 * second click while the URL already ends in the same hash is treated as a
 * full soft navigation and scrolls instantly. A plain <a href="#id"> scrolls
 * fine but leaves a history entry Next ignores on popstate, so Back after a
 * later page change shows the wrong page under the right URL. Pushing the
 * hash through history.pushState goes through Next's patched version, which
 * carries the router state along, so Back and Forward keep working.
 */
export default function InPageLink({ targetId, onClick, ...props }: InPageLinkProps) {
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const target = document.getElementById(targetId);
    if (!target) return;

    event.preventDefault();
    window.history.pushState(null, '', `#${targetId}`);
    target.scrollIntoView();
  };

  return <a {...props} href={`#${targetId}`} onClick={handleClick} />;
}
